<template>
  <div ref="containerRef" class="live-caption-container">
    <div class="live-caption-header">
      <span class="live-indicator">
        <span class="red-dot"></span>
        Live Caption
      </span>
    </div>
    <div class="live-caption-content">
      <transition-group name="caption-fade" tag="div" class="caption-messages">
        <div
          v-for="(message, index) in messages"
          :key="`${message.id}-${index}`"
          class="caption-message"
          :class="message.role.toLowerCase().replace(' ', '-')"
        >
          <span class="speaker-label">{{ message.role }}:</span>
          <span class="message-text">{{ message.text }}</span>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, toRef } from "vue";
import { useLiveCaption } from "@/composables/calls/useLiveCaption";

const props = withDefaults(
  defineProps<{
    isActive?: boolean;
    enabled?: boolean;
    callSid?: string | null;
  }>(),
  {
    isActive: false,
    enabled: true,
    callSid: null,
  }
);

const containerRef = ref<HTMLElement | null>(null);

const { messages } = useLiveCaption({
  callSid: toRef(props, "callSid"),
  isActive: toRef(props, "isActive"),
  enabled: toRef(props, "enabled"),
});

watch(
  () => messages.value.length,
  () => {
    nextTick(() => {
      if (containerRef.value) {
        containerRef.value.scrollTop = containerRef.value.scrollHeight;
      }
    });
  }
);

defineExpose({ displayedMessages: messages });
</script>

<style scoped>
.live-caption-container {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  max-height: 150px;
  overflow-y: auto;
}

.live-caption-header {
  margin-bottom: 8px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  color: #646464;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.red-dot {
  width: 6px;
  height: 6px;
  background-color: #ff0f0f;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.live-caption-content {
  min-height: 40px;
}

.caption-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.caption-message {
  font-size: 14px;
  line-height: 1.6;
  padding: 6px 8px;
  background: #fafafa;
  border-radius: 6px;
  border-left: 3px solid transparent;
}

.caption-message.customer {
  border-left-color: #0066cc;
  text-align: right;
}

.caption-message.sales-rep {
  border-left-color: #cc6600;
  text-align: left;
}

.speaker-label {
  font-weight: 600;
  color: #2c2c2e;
  margin-right: 6px;
}

.caption-message.customer .speaker-label {
  color: #0066cc;
}

.caption-message.sales-rep .speaker-label {
  color: #cc6600;
}

.message-text {
  color: #2c2c2e;
}

.live-caption-container::-webkit-scrollbar {
  width: 6px;
}

.live-caption-container::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.live-caption-container::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.live-caption-container::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

.caption-fade-enter-active {
  transition: all 0.3s ease;
}

.caption-fade-leave-active {
  transition: all 0.2s ease;
}

.caption-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.caption-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
